import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setAuth } from '../Redux/authSlice';
import { Pagination } from 'react-bootstrap';
import { translations } from '../utils/translations';
import { RootState } from '../Redux/store';
import { useSelector } from 'react-redux';

const UserComponent: React.FC = () => {
    const [users, setUsers] = useState<any[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
    const [userRole, setUserRole] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(5); // Items per page
    const [searchTerm, setSearchTerm] = useState<string>('');
    const dispatch = useDispatch();
    const [authenticated, setAuthenticated] = useState<boolean>(false);
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = getCookie('token');
                if (!token) {
                    console.error('No token found in cookies');
                    dispatch(setAuth(false));
                    setError('No token found. Please log in.');
                    return;
                }
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                const usersResponse = await axios.get('https://api.sominnovations.xyz/users/all-users/');
                setUsers(usersResponse.data);
                setFilteredUsers(usersResponse.data); // Initialize filtered users
                fetchUserRole();
            } catch (err) {
                console.error('Error fetching users:', err);
                handleAxiosError(err);
            }
        };

        const fetchUserRole = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get('https://api.sominnovations.xyz/users/user');
                setUserRole(data.user.role);
                setAuthenticated(true);
                console.log('Logged in user role:', data.user.role); // Log user role
            } catch (error) {
                console.error('Failed to fetch user role:', error);
                setError('Failed to fetch user role');
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [dispatch]);

    const getCookie = (name: string) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    };

    const handleDelete = async (userId: number) => {
        if (!window.confirm(t.deleteUserConfirm)) {
            return;
        }

        try {
            const token = getCookie('token');
            if (!token) {
                throw new Error('No token found in cookies');
            }
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            await axios.delete(`https://api.sominnovations.xyz/users/users/${userId}/`);
            const updatedUsers = users.filter(user => user.id !== userId);
            setUsers(updatedUsers);
            setFilteredUsers(updatedUsers); // Update filtered users
        } catch (err) {
            console.error('Error deleting user:', err);
            handleAxiosError(err);
        }
    };

    const handleAxiosError = (err: any) => {
        if (axios.isAxiosError(err)) {
            if (err.response) {
                setError(`Error: ${err.response.status} ${err.response.statusText}`);
            } else if (err.request) {
                setError('Error: No response received from the server');
            } else {
                setError(`Error: ${err.message}`);
            }
        } else {
            setError('An unexpected error occurred');
        }
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        const filtered = users.filter((user) =>
            `${user.first_name} ${user.last_name}`.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredUsers(filtered);
        setCurrentPage(1);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

    // Render nothing if userRole is not admin or super_admin
    if (userRole !== 'admin' && userRole !== 'super_admin') {
        return (
            <div className="container" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                <div className="row">
                    <div className="col-md-12">
                        <div className="alert alert-danger">
                            {t.unauthorizedAccess}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-3">
            <div className="mb-4" style={{ textAlign: "center" }}>
                <h4 className="text-primary">{t.userManagement}</h4>
            </div>
            {error && <div className="alert alert-danger">{error}</div>}
            {loading ? (
                <div className="text-center">
                    <p>{t.loading}</p>
                </div>
            ) : (
                <div className="table-responsive shadow-sm">
                    <table className="table table-striped table-hover table-bordered">
                        <thead className="thead-dark">
                            <tr>
                                <th>{t.firstName}</th>
                                <th>{t.lastName}</th>
                                <th>{t.email}</th>
                                <th>{t.role}</th>
                                <th>{t.branchName}</th>
                                <th>{t.actions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(user => (
                                <tr key={user.id}>
                                    <td>{user.first_name}</td>
                                    <td>{user.last_name}</td>
                                    <td>{user.email}</td>
                                    <td>{user.role}</td>
                                    <td>{user.branch_name}</td>
                                    <td>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => handleDelete(user.id)}
                                        >
                                            {t.delete}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="d-flex justify-content-center mt-3">
                <Pagination>
                    {[...Array(totalPages)].map((_, index) => (
                        <Pagination.Item
                            key={index}
                            active={index + 1 === currentPage}
                            onClick={() => setCurrentPage(index + 1)}
                        >
                            {index + 1}
                        </Pagination.Item>
                    ))}
                </Pagination>
            </div>
        </div>
    );
};

export default UserComponent;
